import { useMutation, useQuery } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { apiClient, unwrap } from '@/lib/api-client';
import { createCrudHooks } from './create-crud-hooks';

export const {
  keys: termAcceptanceKeys,
  useList: useTermAcceptances,
  useDetail: useTermAcceptance,
  useCreate: useCreateTermAcceptance,
  useUpdate: useUpdateTermAcceptance,
  useDelete: useDeleteTermAcceptance,
} = createCrudHooks(apiClient['term-acceptances'], 'term-acceptances');

export type SoldierTermAcceptance = {
  id: string;
  acceptedBy: 'participant' | 'guardian';
  acceptedAt: string;
  ip: string | null;
  port: number | null;
  userAgent: string | null;
  pdfHash: string;
  kind: 'participation' | 'minor_authorization';
  version: number;
  title: string;
};

/** Aceites de um recruta (`GET /term-acceptances/soldier/:id`) — bloco "Termos aceitos" da ficha. */
export function useSoldierTermAcceptances(soldierId?: string) {
  return useQuery({
    queryKey: [...termAcceptanceKeys.all, 'soldier', soldierId],
    queryFn: async () => {
      const res = await (apiClient['term-acceptances'] as any)
        .soldier({ soldierId })
        .get();
      return unwrap(res.data) as SoldierTermAcceptance[];
    },
    enabled: !!soldierId,
  });
}

/** PDF do aceite, servido pela sessão do painel (mesma origem, cookie vai junto). */
export const termAcceptancePdfUrl = (id: string) => `/api/v1/term-acceptances/${id}/pdf`;

export type TermInvite = { url: string; expiresAt: string };

/**
 * Link do site pra uma inscrição antiga (sem aceite) ler e aceitar os termos
 * (`GET /term-acceptances/soldier/:id/invite`). Vale 7 dias e só até o aceite ser registrado.
 */
export function useTermInvite() {
  const toast = useToast();

  return useMutation({
    mutationFn: async (soldierId: string) => {
      const res = await (apiClient['term-acceptances'] as any)
        .soldier({ soldierId })
        .invite.get();
      if (res.error) {
        const value = res.error.value as {
          message?: string;
          errors?: Record<string, string>;
        };
        throw { message: Object.values(value?.errors ?? {})[0] ?? value?.message };
      }
      return unwrap(res.data) as TermInvite;
    },
    onError: (e: any) => {
      toast.error(e.message ?? 'Não foi possível gerar o link dos termos.');
    },
  });
}
